import { useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { AssetThumbnail } from '../AssetThumbnail';
import type { AssetItem, PendingUpload } from '../types';

interface AssetGridProps {
  assets: AssetItem[];
  columns: 2 | 4;
  showControls: boolean | ((item: AssetItem) => boolean);
  pendingUploads?: PendingUpload[];
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelect?: (id: string, selected: boolean) => void;
  onAssetClick?: (asset: AssetItem) => void;
  onDelete?: (id: string) => void;
  onRename?: (id: string, newName: string) => void;
  onChangeCategory?: (id: string) => void;
  onDragStart?: (e: React.DragEvent, asset: AssetItem) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

export const AssetGrid = ({
  assets, columns, showControls, pendingUploads = [],
  selectable, selectedIds, onSelect,
  onAssetClick, onDelete, onRename, onChangeCategory, onDragStart,
  hasMore, isLoadingMore, onLoadMore,
}: AssetGridProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore || !onLoadMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onLoadMore(); },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  const gridClass = columns === 4
    ? 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3'
    : 'grid grid-cols-2 gap-1.5';

  const resolveShowControls = (item: AssetItem) =>
    typeof showControls === 'function' ? showControls(item) : showControls;

  return (
    <div>
      <div className={gridClass}>
        {/* Pending uploads first */}
        {pendingUploads.map(p => (
          <AssetThumbnail
            key={p.id}
            src={p.localUrl}
            name={p.fileName}
            showControls={false}
            isPending
            pendingStatus={p.status}
          />
        ))}
        {/* Existing assets */}
        {assets.map(asset => (
          <AssetThumbnail
            key={asset.id}
            src={asset.src}
            name={asset.name}
            size={asset.size}
            mimeType={asset.mimeType}
            hasWarning={asset.hasWarning}
            warningText={asset.warningText}
            showControls={resolveShowControls(asset)}
            selectable={selectable}
            selected={selectedIds?.has(asset.id)}
            onSelect={(sel) => onSelect?.(asset.id, sel)}
            onClick={() => onAssetClick?.(asset)}
            onDelete={() => onDelete?.(asset.id)}
            onRename={(newName) => onRename?.(asset.id, newName)}
            onChangeCategory={onChangeCategory ? () => onChangeCategory(asset.id) : undefined}
            onDragStart={(e) => onDragStart?.(e, asset)}
          />
        ))}
      </div>
      {hasMore && (
        <div ref={sentinelRef} className="flex items-center justify-center h-10 mt-2">
          {isLoadingMore && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </div>
      )}
    </div>
  );
};
